import { useAppRouter } from '@/common/route/useAppRouter';
import { StringUtils } from '@/common/utils/stringUtils';
import { useCrudActions } from '@/common/view/useCrudActions';
import { ROUTE_NAME_PROGRESS } from '@/modules/progress/router';
import { progressService } from '@/services/progress';
import { useVuelidate } from '@vuelidate/core';
import { required, requiredIf } from '@vuelidate/validators';
import { computed, reactive, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { ProgressLevelType } from './mixins';

export function useProgressLevelAdd() {
    const route = useRoute();
    const router = useAppRouter();
    const crudAction = useCrudActions();

    const linkableItems = ref([] as Array<{ type: number; itemId: number; itemName: string }>);
    const linkableItemOption = computed(() =>
        linkableItems.value
            .filter(item => model.type && item.type === model.type)
            .map(item => ({ value: item.itemId, label: item.itemName }))
    );

    const model = reactive({
        progressId: StringUtils.toNumber(route.params.progressId),
        progressName: null,
        name: '',
        type: null,
        reward: null,
        quantity: null,
        itemId: null,
        imageFile: null,
        get isRewardLinkableItem() {
            return (
                model.type === ProgressLevelType.Promotion.value ||
                model.type === ProgressLevelType.PromotionBooster.value
            );
        }
    });

    const validations = {
        name: { required },
        type: { required },
        reward: { requiredIf: requiredIf(() => !model.isRewardLinkableItem) },
        quantity: { requiredIf: requiredIf(() => !model.isRewardLinkableItem) },
        itemId: { requiredIf: requiredIf(() => model.isRewardLinkableItem) }
    };

    const v$ = useVuelidate(validations, model);

    const fetch = async () => {
        const fetchItem = crudAction.executeAction({
            canExecute: () => model.progressId != null,
            request: () => progressService.item.get(model.progressId),
            onSuccess: ({ data }) => (model.progressName = data?.name || StringUtils.Characters.Empty),
            onError: () => router.push({ name: ROUTE_NAME_PROGRESS.ITEM.INDEX })
        });

        const fetchLinkableList = crudAction.executeAction({
            canExecute: () => model.progressId != null,
            request: () => progressService.level.listLinkableItem(model.progressId),
            onSuccess: ({ data }) => {
                linkableItems.value.push(...data);
            },
            onError: () => router.push({ name: ROUTE_NAME_PROGRESS.ITEM.INDEX })
        });

        Promise.all([fetchItem(), fetchLinkableList()]);
    };

    const create = crudAction.excuteAndNotify({
        validator: v$,
        request: () =>
            progressService.level.create(
                model.progressId,
                model.name,
                model.type,
                model.reward,
                model.quantity,
                model.itemId,
                model.imageFile
            ),
        onSuccess: () =>
            router.push({ name: ROUTE_NAME_PROGRESS.LEVEL.INDEX, params: { progressId: model.progressId } })
    });

    watch(
        () => model.type,
        () => (model.itemId = null)
    );

    return { model, v$, create, fetch, linkableItemOption };
}
